import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { PageHero } from "@/pages/ServicesPage";
import { AREAS, IMAGES, TRADES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXPERIENCE = ["1–3 years", "3–5 years", "5–10 years", "10+ years"];

export default function ContractorPage() {
  const [form, setForm] = useState({ full_name: "", company: "", email: "", phone: "", experience: "", message: "" });
  const [trades, setTrades] = useState([]);
  const [areas, setAreas] = useState([]);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const startedAt = useRef(Date.now());
  const honeypot = useRef("");

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!trades.length) { setError("Please select at least one trade."); return; }
    if (!consent) { setError("Please accept the privacy consent to continue."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contractor-applications`, { ...form, trades, areas, consent, website: honeypot.current, started_at: startedAt.current });
      setSubmitted(true);
      toast.success("Your application has been received.");
      window.scrollTo({ top: 0 });
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contractor-page">
      <SEO
        title="Contractor Application | Partner With Estate Outdoors Calgary"
        description="Landscape designers, builders and specialized trades: apply to collaborate with Estate Outdoors Calgary on estate projects across Springbank, Bearspaw and Calgary."
      />
      <PageHero
        testid="contractor-hero"
        eyebrow="For Contractors"
        title="Do Your Finest Work, With the Right Clients"
        subtitle="We occasionally collaborate with exceptional specialized trades on estate projects across Calgary and Rocky View County."
        image={IMAGES.brownstone}
      />

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {submitted ? (
            <Reveal>
              <div data-testid="contractor-success" className="border border-[#C5A059]/50 bg-white p-10 sm:p-14 text-center">
                <CheckCircle2 size={44} strokeWidth={1.2} className="mx-auto text-[#6B7C6E]" aria-hidden="true" />
                <h2 className="mt-6 font-display text-3xl font-light">Thank You for Applying.</h2>
                <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#4A524C]">
                  We review every application personally. If your work aligns with our clients' projects, we'll be in touch.
                </p>
              </div>
            </Reveal>
          ) : (
            <form onSubmit={submit} data-testid="contractor-form" className="border border-[#E0D8CC] bg-white p-7 sm:p-12">
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] top-0 h-0 w-0 opacity-0" onChange={(e) => { honeypot.current = e.target.value; }} />

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="c-name" className="field-label">Full Name *</label>
                  <input id="c-name" data-testid="contractor-name-input" required className="field-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} autoComplete="name" />
                </div>
                <div>
                  <label htmlFor="c-company" className="field-label">Company *</label>
                  <input id="c-company" data-testid="contractor-company-input" required className="field-input" value={form.company} onChange={(e) => set("company", e.target.value)} autoComplete="organization" />
                </div>
                <div>
                  <label htmlFor="c-email" className="field-label">Email *</label>
                  <input id="c-email" data-testid="contractor-email-input" required type="email" className="field-input" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="c-phone" className="field-label">Phone *</label>
                  <input id="c-phone" data-testid="contractor-phone-input" required type="tel" className="field-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                </div>
              </div>

              <div className="mt-8">
                <span className="field-label">Trades & Specialties *</span>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Trades">
                  {TRADES.map((t) => (
                    <button key={t} type="button" data-testid={`trade-pill-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} data-selected={trades.includes(t)} aria-pressed={trades.includes(t)} onClick={() => toggle(trades, setTrades, t)} className="pill-option">
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <span className="field-label">Areas You Serve</span>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Service areas">
                  {AREAS.map((a) => (
                    <button key={a.name} type="button" data-testid={`area-pill-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} data-selected={areas.includes(a.name)} aria-pressed={areas.includes(a.name)} onClick={() => toggle(areas, setAreas, a.name)} className="pill-option">
                      {a.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <label htmlFor="c-experience" className="field-label">Years of Experience *</label>
                <select id="c-experience" data-testid="contractor-experience-select" required className="field-input" value={form.experience} onChange={(e) => set("experience", e.target.value)}>
                  <option value="">Select…</option>
                  {EXPERIENCE.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>

              <div className="mt-8">
                <label htmlFor="c-message" className="field-label">Tell Us About Your Work</label>
                <textarea id="c-message" data-testid="contractor-message-input" rows={5} className="field-input resize-y" placeholder="Notable projects, team size, insurance and WCB status, what kind of work you love…" value={form.message} onChange={(e) => set("message", e.target.value)} />
              </div>

              <label className="mt-8 flex cursor-pointer items-start gap-3 text-sm text-[#4A524C]">
                <input type="checkbox" data-testid="contractor-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#8B5A2B]" />
                <span>I consent to Estate Outdoors Calgary contacting me about partnership and storing my details as described in the Privacy Policy. *</span>
              </label>

              {error && (
                <p data-testid="contractor-form-error" role="alert" className="mt-6 border border-[#9c3d33]/40 bg-[#9c3d33]/5 px-4 py-3 text-sm text-[#9c3d33]">{error}</p>
              )}

              <button type="submit" data-testid="contractor-submit-btn" disabled={submitting} className="btn-primary mt-9 w-full disabled:opacity-60">
                {submitting ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>) : "Submit Application"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
